import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Signal, MapPin, Lock, Check, X, Trophy, Zap, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { trackPrediction } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";

const ALL_MARKETS = [
  { id: "even_balls", label: "01 · Double Runs", question: "Will there be double runs (a 2) in the over?" },
  { id: "first_ball_scoring", label: "02 · 1st Ball Scores", question: "Will the 1st ball of the over be a scoring ball?" },
  { id: "even_runs", label: "03 · Even Over Runs", question: "Even number of runs in the over?" },
  { id: "boundary", label: "04 · Boundary", question: "Boundary (4 or 6) in the over?" },
  { id: "six", label: "05 · Six", question: "A six in the over?" },
  { id: "dot_balls_gt3", label: "06 · 3+ Dot Balls", question: "More than 3 dot balls in the over?" },
  { id: "wicket", label: "07 · Wicket", question: "Wicket in the over?" },
  { id: "runs_gt7", label: "08 · 7+ Runs", question: "More than 7 runs in the over?" },
];

const BUCKETS = [
  { key: "0", label: "0", sub: "Maiden" },
  { key: "1-3", label: "1–3", sub: "Rotate" },
  { key: "4-6", label: "4–6", sub: "Boundary" },
  { key: "7+", label: "7+", sub: "Carnage" },
];

export default function LiveMatchDetailPage() {
  const { id } = useParams();
  const { user, setAuthOpen, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bucket, setBucket] = useState(null);
  const [wicketPick, setWicketPick] = useState(null);
  const [submittingMarket, setSubmittingMarket] = useState(null);
  const [submittingOver, setSubmittingOver] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/live/matches/${id}/predictable`);
      setData(r.data);
      if (r.data.my_over_pick) {
        setBucket(r.data.my_over_pick.runs_bucket);
        setWicketPick(r.data.my_over_pick.wicket_prediction);
      } else {
        setBucket(null);
        setWicketPick(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load match");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const pickMarket = async (marketId, ans) => {
    if (!user) { setAuthOpen(true); return; }
    setSubmittingMarket(marketId);
    try {
      await api.post(`/live/matches/${id}/market-predictions`, { market_id: marketId, answer: ans });
      trackPrediction({ match_type: "live", market: marketId, answer: String(ans) });
      toast.success(`Locked ${ans} on ${marketId}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pick failed");
    } finally {
      setSubmittingMarket(null);
    }
  };

  const submitOverPick = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!bucket || wicketPick === null) return;
    setSubmittingOver(true);
    try {
      await api.post(`/live/matches/${id}/predictions`, { runs_bucket: bucket, wicket_prediction: wicketPick });
      trackPrediction({ match_type: "live", market: "over_runs_wicket", over: data.upcoming_over });
      toast.success(`Locked over #${data.upcoming_over}`);
      await load();
      if (user) { try { await refresh(); } catch (_) {} }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Over pick failed");
    } finally {
      setSubmittingOver(false);
    }
  };

  if (loading && !data) return <div className="p-12 text-slate-400">Loading live match…</div>;
  if (error) return <div className="p-12"><div className="tile p-6 border-rose-500/40 text-rose-300">{error}</div></div>;
  if (!data) return null;

  const { match, state, upcoming_over, resolvable_markets, my_market_picks, my_over_pick } = data;
  const [a, b] = match.teams || ["", ""];
  const pickedByMarket = Object.fromEntries((my_market_picks || []).map((p) => [p.market_id, p]));
  const overLocked = Boolean(my_over_pick);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <Link to="/live" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6" data-testid="back-to-live-feed">
        <ArrowLeft className="w-4 h-4" /> Back to live feed
      </Link>

      <div className="tile p-6 lg:p-8 grain mb-6 relative overflow-hidden">
        <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center flex-wrap gap-2 mb-3">
            <span className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
              <span className="live-dot" /> Live · Real Feed
            </span>
            <span className="pill bg-amber-500/15 text-amber-300 border border-amber-500/30">{match.matchType}</span>
            {match.venue && <span className="text-xs text-slate-400 flex items-center gap-1 ml-1"><MapPin className="w-3 h-3" />{match.venue}</span>}
          </div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">
            {match.name}
          </h1>
          <div className="text-slate-400 text-sm mt-2">{match.status}</div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="section-label">Score</div>
              <div className="metric-value text-4xl text-emerald-400 mt-1">{state.runs}/{state.wickets}</div>
              <div className="font-mono-data text-slate-400 text-sm mt-0.5">Overs {state.overs}</div>
            </div>
            <div>
              <div className="section-label">Batting</div>
              <div className="text-slate-200 mt-1 font-display font-bold text-lg">{state.inning || `${a} innings`}</div>
            </div>
            <div>
              <div className="section-label">Next over</div>
              <div className="metric-value text-4xl text-amber-400 mt-1">#{upcoming_over}</div>
              <div className="font-mono-data text-slate-500 text-[11px] mt-0.5">Poller resolves every 20 min</div>
            </div>
          </div>
        </div>
      </div>

      <div className="tile p-4 mb-6 border-amber-500/30 bg-amber-500/5 text-amber-200 text-sm flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          Real match resolution runs on the free tier of cricketdata.org. Only <b>3 of 8 markets</b> can be scored (Even Runs · Wicket · 7+ Runs). The rest are locked. Points settle within ~20 minutes of the over completing.
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-6">
          {/* 8-market grid */}
          <div className="tile p-6 grain">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="section-label">Live probability picks · +5 pts each</div>
                <div className="font-display font-black uppercase text-lg tracking-tight">Predictions Panel — Over #{upcoming_over}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {ALL_MARKETS.map((m) => {
                const isResolvable = resolvable_markets.includes(m.id);
                const pick = pickedByMarket[m.id];
                return (
                  <MarketCard
                    key={m.id}
                    market={m}
                    disabled={!isResolvable}
                    pick={pick}
                    isSubmitting={submittingMarket === m.id}
                    onPick={(ans) => pickMarket(m.id, ans)}
                    signedIn={Boolean(user)}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
          {/* Runs bucket + wicket over pick */}
          <div className="tile p-6 grain">
            <div className="flex items-center justify-between mb-4">
              <div className="section-label">Your call · over #{upcoming_over}</div>
              <div className="font-mono-data text-amber-400 text-sm">+10/+8/+12</div>
            </div>

            {overLocked && (
              <div className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 mb-3">
                <Check className="w-3.5 h-3.5" /> Locked
              </div>
            )}

            <div className="section-label mb-2">Runs bucket</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {BUCKETS.map((bk) => (
                <button
                  key={bk.key}
                  disabled={overLocked || submittingOver || !user}
                  onClick={() => setBucket(bk.key)}
                  className={`p-2.5 rounded-lg border text-left transition-all ${bucket === bk.key ? "ring-2 ring-amber-400 border-amber-400 bg-amber-500/10" : "bg-white/5 border-white/10 hover:bg-white/10"} ${overLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                  data-testid={`live-bucket-${bk.key}`}
                >
                  <div className="font-display font-black text-xl uppercase">{bk.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{bk.sub}</div>
                </button>
              ))}
            </div>

            <div className="section-label mb-2">Wicket?</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                disabled={overLocked || submittingOver || !user}
                onClick={() => setWicketPick(true)}
                className={`p-2 rounded-lg border ${wicketPick === true ? "ring-2 ring-rose-400 border-rose-400 bg-rose-500/15 text-rose-200" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"} ${overLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid="live-wicket-yes"
              >
                <div className="font-display font-black text-sm uppercase">Yes</div>
              </button>
              <button
                disabled={overLocked || submittingOver || !user}
                onClick={() => setWicketPick(false)}
                className={`p-2 rounded-lg border ${wicketPick === false ? "ring-2 ring-emerald-400 border-emerald-400 bg-emerald-500/15 text-emerald-200" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"} ${overLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid="live-wicket-no"
              >
                <div className="font-display font-black text-sm uppercase">No</div>
              </button>
            </div>

            <button
              onClick={submitOverPick}
              disabled={overLocked || submittingOver || !bucket || wicketPick === null || !user}
              className="btn-primary w-full"
              data-testid="live-submit-over-pick"
            >
              {overLocked ? "Locked in" : submittingOver ? "Locking…" : user ? "Lock prediction" : "Sign in to pick"}
            </button>

            {my_over_pick && my_over_pick.status !== "pending" && (
              <div className={`mt-3 text-xs ${my_over_pick.status === "won" ? "text-emerald-300" : "text-rose-300"}`}>
                Resolved · Actual {my_over_pick.actual_bucket} runs, {my_over_pick.actual_wicket ? "wicket" : "no wicket"} → +{my_over_pick.points_earned} pts
              </div>
            )}
          </div>

          {!user && (
            <button className="btn-primary w-full" onClick={() => setAuthOpen(true)} data-testid="live-signin-cta">
              Sign in to predict
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MarketCard({ market, disabled, pick, isSubmitting, onPick, signedIn }) {
  const locked = Boolean(pick);
  const resolved = pick && pick.status !== "pending";
  const won = pick?.status === "won";

  return (
    <div
      className={`relative tile-elev p-4 overflow-hidden ${disabled ? "opacity-50" : ""} ${resolved ? (won ? "ring-1 ring-emerald-400/60" : "ring-1 ring-rose-500/40") : ""}`}
      data-testid={`live-market-card-${market.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="section-label">{market.label}</div>
        {disabled && (
          <span className="pill bg-slate-800 text-slate-500 border border-slate-700 text-[9px]">
            <Lock className="w-2.5 h-2.5" /> N/A
          </span>
        )}
        {resolved && !disabled && (
          <span className={`pill ${won ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border border-rose-500/40"}`}>
            {won ? <><Trophy className="w-3 h-3" /> +{pick.points_earned}</> : "Missed"}
          </span>
        )}
      </div>
      <div className="text-sm text-slate-200 mt-1.5 leading-snug min-h-[2.5rem]">{market.question}</div>

      {disabled ? (
        <div className="mt-3 text-[10px] text-slate-500 italic">Needs ball-by-ball data</div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => onPick("Yes")}
            disabled={locked || isSubmitting}
            className={`text-xs font-bold uppercase tracking-wider py-2 rounded-lg border transition-all
              ${pick?.answer === "Yes" ? "bg-emerald-500/25 border-emerald-400 text-emerald-200" : "bg-emerald-500/5 border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/15"}
              ${locked ? "cursor-not-allowed" : ""}`}
            data-testid={`live-market-pick-${market.id}-yes`}
          >
            {pick?.answer === "Yes" && <Lock className="w-3 h-3 inline mr-1" />}Yes
          </button>
          <button
            type="button"
            onClick={() => onPick("No")}
            disabled={locked || isSubmitting}
            className={`text-xs font-bold uppercase tracking-wider py-2 rounded-lg border transition-all
              ${pick?.answer === "No" ? "bg-rose-500/25 border-rose-400 text-rose-200" : "bg-rose-500/5 border-rose-500/25 text-rose-300 hover:bg-rose-500/15"}
              ${locked ? "cursor-not-allowed" : ""}`}
            data-testid={`live-market-pick-${market.id}-no`}
          >
            {pick?.answer === "No" && <Lock className="w-3 h-3 inline mr-1" />}No
          </button>
        </div>
      )}

      {resolved && !disabled && pick.correct_answer && (
        <div className="mt-2 text-[10px] font-mono-data text-slate-400">
          Actual: <span className={won ? "text-emerald-300" : "text-rose-300"}>{pick.correct_answer}</span>
        </div>
      )}
      {!disabled && !signedIn && (
        <div className="mt-2 text-[10px] text-slate-500">Sign in to lock a pick</div>
      )}
    </div>
  );
}
